import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { InterviewScope } from '@/types/candidates';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { CandidateInterviewDialog } from './candidate-interview-dialog';
import { CandidateInterviewList } from './candidate-interview-list';
import { useCandidate } from '@/hooks/use-candidates';
import { isConsentLocked } from '../lib/consent-actions';

interface CandidateInterviewTabProps {
  candidateId: string;
}

const SCOPES: Array<{ value: InterviewScope; label: string }> = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'all', label: 'All' },
];

export function CandidateInterviewTab({ candidateId }: CandidateInterviewTabProps) {
  const [scope, setScope] = useState<InterviewScope>('upcoming');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  // HW-1055: no new interview with a candidate who revoked consent.
  const locked = isConsentLocked(useCandidate(candidateId).data);

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-medium">Interviews</h2>
          <p className="text-sm text-muted-foreground">
            Schedule calls, meetings and feedback rounds with the candidate.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-md border border-border bg-background p-0.5 text-xs">
            {SCOPES.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => setScope(s.value)}
                className={cn(
                  'rounded-sm px-2.5 py-1 transition-colors',
                  scope === s.value
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {s.label}
              </button>
            ))}
          </div>
          <Button
            type="button"
            size="sm"
            onClick={() => setScheduleOpen(true)}
            disabled={locked}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Schedule interview
          </Button>
        </div>
      </div>

      <CandidateInterviewList candidateId={candidateId} scope={scope} />

      <CandidateInterviewDialog
        candidateId={candidateId}
        interview={null}
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
      />
    </section>
  );
}
